import { format } from 'date-fns';
import { forEach } from 'lodash';
import {
  externalOr,
  logStatusDisplay,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client-model-helpers';
import {
  FlightAdvantage,
  FlightLogEntryDto,
  LogMode,
  LogStatus,
  MaintenanceSummaryDto,
  WaypointDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightOutcome } from 'src/app/shared/flight-log-api/flights/flight-client.model';
import {
  CrewMemberDto,
  CrewRole,
  FlightStatus,
  FlightType,
  flightTypeDisplay,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { AircraftEntity } from 'src/store/aircraft/aircraft.model';
import { BaseEntity } from 'src/store/base/base.model';
import { CompanyEntity } from 'src/store/company/company.model';
import { FlightLogSummaryEntity } from 'src/store/flight-log-summary/flight-log-summary.model';
import { FlightLogCommonProps, FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import { humanize } from 'src/util/text';

class FlightLogViewModelBase implements FlightLogCommonProps {
  private readonly _jsonValue: any;
  public id: string;
  public flightSummaryId?: string;
  public companyId?: string;
  public baseId?: string;
  public aircraftId?: string;
  public advantages: FlightAdvantage[];
  public flightCrew: CrewMemberDto[];
  public maintenanceSummary: MaintenanceSummaryDto;
  public status: LogStatus;
  public mode: LogMode;
  public flightType?: FlightType;
  public flightTagNumber?: string;
  public flightTime?: Date;
  public updatedBy?: string;
  public comment?: string;
  public deletedAt?: Date;
  public fromLocation?: string;
  public toLocation?: string;
  constructor(props: FlightLogCommonProps) {
    this._jsonValue = props;
    Object.assign(this, props);
  }

  get pilotInCommand() {
    return this.flightCrew.find((f) => f.role === CrewRole.PilotInCommand);
  }

  get flightSummaryIdDisplay() {
    return externalOr(this.mode, this.flightSummaryId);
  }

  get logStatusDisplay() {
    return logStatusDisplay(this.status);
  }
  get flightTypeDisplay() {
    return this.flightType != null ? flightTypeDisplay(this.flightType) : 'Unknown';
  }
  get flightTagNumberDisplay() {
    return externalOr(this.mode, this.flightTagNumber != null ? this.flightTagNumber : null);
  }

  public toJSON() {
    return this._jsonValue;
  }
}
const getCyclesFn = (cycles: { [key: string]: number }) => {
  let engineCycles = '';
  forEach(cycles, (val, key) => {
    engineCycles += val != null ? ` ${key}:${val},` : '';
  });
  return engineCycles.trim().slice(0, -1);
};
export class FlightLogSummaryViewModel
  extends FlightLogViewModelBase
  implements FlightLogSummaryEntity {
  public pilotName?: string;
  public pilotEmployeeId?: string;
  public coPilotEmployeeId?: string;
  public isUnassigned: boolean;
  public dispatchId?: string;
  public dateOfService?: Date;
  public flightStatus: FlightStatus;
  public flightOutcome: FlightOutcome;
  public programId?: string;
  public aircraft?: AircraftEntity;
  public company?: CompanyEntity;
  public transportBase?: BaseEntity;

  constructor(flightLogSummary: FlightLogSummaryEntity) {
    super(flightLogSummary);
    Object.assign(this, flightLogSummary);
  }

  get effectiveDateOfService(): Date | null | undefined {
    return this.flightTime != null ? this.flightTime : this.dateOfService;
  }

  get dateofServiceDisplay(): string | undefined {
    return this.effectiveDateOfService != null
      ? format(this.effectiveDateOfService, 'MM/DD/YYYY HH:mm')
      : undefined;
  }

  get tailNumberDisplay() {
    return externalOr(this.mode, this.aircraft != null ? this.aircraft.tailNumber : null);
  }

  get flightStatusDisplay() {
    return this.flightStatus != null ? humanize(this.flightStatus) : 'Unknown';
  }

  get engine1CyclesDisplay() {
    if (this.maintenanceSummary != null) {
      return getCyclesFn(this.maintenanceSummary.engine1.cycles);
    }
    return undefined;
  }

  get engine2CyclesDisplay() {
    if (this.maintenanceSummary != null) {
      return getCyclesFn(this.maintenanceSummary.engine2.cycles);
    }
    return undefined;
  }

  public static fromJS(flightLog: FlightLogSummaryEntity): FlightLogSummaryViewModel {
    if (flightLog instanceof FlightLogSummaryViewModel) {
      return flightLog;
    }
    return new FlightLogSummaryViewModel(flightLog);
  }
}

export class FlightLogViewModel extends FlightLogViewModelBase implements FlightLogEntity {
  public waypoints: WaypointDto[];
  public entries: FlightLogEntryDto[];
  public metadata: any;
  constructor(flightLog: FlightLogEntity) {
    super(flightLog);
    Object.assign(this, flightLog);
  }

  public static fromJS(flightLog: FlightLogEntity): FlightLogViewModel {
    if (flightLog instanceof FlightLogViewModel) {
      return flightLog;
    }
    return new FlightLogViewModel(flightLog);
  }
}
